import { apiSend } from './client'
import { mediaItemsFromPost, type BskyPost } from './media'
import { useBsky } from '../store/bsky'
import type { MediaItem } from '../types'

interface SearchResp {
  posts?: BskyPost[]
  cursor?: string
}

export class RateLimitedError extends Error {
  constructor() {
    super('bluesky rate limit')
    this.name = 'RateLimitedError'
  }
}

export class NotConnectedError extends Error {
  constructor() {
    super('connect your Bluesky account to load content')
    this.name = 'NotConnectedError'
  }
}

const isExpired = (m: string) =>
  /ExpiredToken|jwt expired|invalid_token|InvalidToken|\b401\b/i.test(m)
const isRateLimit = (m: string) => /\b429\b|RateLimit|rate limit/i.test(m)

const viaXrpc = (path: string, accessJwt: string, service: string) =>
  apiSend<SearchResp>('/api/xrpc', { body: { service, accessJwt, path, method: 'GET' } })

const viaAppView = (path: string) => apiSend<SearchResp>('/api/appview', { body: { path } })

/** Paginated Bluesky search — AppView first (reliable), authed PDS as fallback. */
export async function searchTagPage(
  query: string,
  tagLabel: string,
  opts: { cursor?: string; limit?: number; sort?: 'top' | 'latest' } = {},
): Promise<{ items: MediaItem[]; cursor?: string }> {
  const qp = new URLSearchParams({
    q: query.trim(),
    sort: opts.sort ?? 'latest',
    limit: String(opts.limit ?? 25),
  })
  if (opts.cursor) qp.set('cursor', opts.cursor)
  const path = `app.bsky.feed.searchPosts?${qp.toString()}`

  let resp: SearchResp | undefined
  let lastErr: unknown

  // AppView is the reliable path for searchPosts (public.api.bsky.app 403s).
  try {
    resp = await viaAppView(path)
  } catch (e) {
    lastErr = e
    const msg = e instanceof Error ? e.message : String(e)
    if (isRateLimit(msg)) throw new RateLimitedError()
  }

  if (!resp?.posts?.length) {
    const session = useBsky.getState().session
    if (session) {
      try {
        resp = await viaXrpc(path, session.accessJwt, session.service)
      } catch (e) {
        lastErr = e
        const msg = e instanceof Error ? e.message : String(e)
        if (isExpired(msg)) {
          try {
            const fresh = await useBsky.getState().refresh()
            resp = await viaXrpc(path, fresh.accessJwt, fresh.service)
          } catch {
            /* keep lastErr */
          }
        }
      }
    }
  }

  if (!resp) {
    const msg = lastErr instanceof Error ? lastErr.message : String(lastErr ?? 'search failed')
    if (isRateLimit(msg)) throw new RateLimitedError()
    throw lastErr instanceof Error ? lastErr : new Error(msg)
  }

  return {
    items: (resp.posts ?? []).flatMap((p) => mediaItemsFromPost(p, tagLabel)),
    cursor: resp.cursor,
  }
}